import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  Typography,
  Grid,
  Select,
  MenuItem,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { PictureAsPdf } from "@mui/icons-material";
import { API_URL } from "../../../config";
import Cookies from "js-cookie";
import SKUImageManager from "./SKUImageManager";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Pagination from "../../../Common_Components/Pagination";
import { useParams } from "react-router-dom";

const USER_ID = Cookies.get("id");
const ROLE = Cookies.get("role");

const SKU = () => {
  const { id: branchIdFromUrl } = useParams(); // get branchId from URL

  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    p_name: "",
    description: "",
    qty: "",
    entry_date: "",
    exit_date: "",
    exit_note : "",
    images: [],
    imagePreviews: [],
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ---------------- Fetch SKUs ----------------
  const fetchSKUs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/getSKUs`, {
        params: {
          page,
          limit,
          search,
          sortBy,
          sortOrder,
          branchId: branchIdFromUrl,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
      });
      setSkus(res.data.skus);
      setTotalEntries(res.data.total);
      setTotalPages(res.data.totalPages);
      setLoading(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch SKUs");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSKUs();
  }, [page, limit, search, sortBy, sortOrder, branchIdFromUrl]);

  // ---------------- Handlers ----------------
  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === "asc";
    setSortBy(field);
    setSortOrder(isAsc ? "desc" : "asc");
  };

  const handleOpenModal = (sku = null) => {
    if (sku) {
      setIsEditing(true);
      setEditId(sku._id);
      setFormData({
        p_name: sku.p_name || "",
        description: sku.description || "",
        qty: sku.qty || "",
        exit_note: sku.exit_note || "",
        entry_date: sku.entry_date ? sku.entry_date.split("T")[0] : "",
        exit_date: sku.exit_date ? sku.exit_date.split("T")[0] : "",
        images: [],
        imagePreviews: sku.images?.map((img) => img) || [],
      });
    } else {
      setIsEditing(false);
      setEditId(null);
      setFormData({
        p_name: "",
        description: "",
        qty: "",
        entry_date: "",
        exit_date: "",
        exit_note: "",
        images: [],
        imagePreviews: [],
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleImageChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...newFiles],
      imagePreviews: [...prev.imagePreviews, ...newPreviews],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Build payload as JSON for update
      const payload = {
        p_name: formData.p_name,
        branchId: branchIdFromUrl,
        userId: USER_ID,
        description: formData.description || "",
        qty: formData.qty || 0,
        entry_date: formData.entry_date || null,
        exit_date: formData.exit_date || null,
        exit_note: formData.exit_note || null,
      };

      if (isEditing) {
        const res = await axios.put(`${API_URL}/updateSKU/${editId}`, payload);
        toast.success(res.data.message || "SKU updated successfully");
      } else {
        // For create, if API expects FormData for images, use FormData
        const data = new FormData();
        Object.keys(payload).forEach((key) => data.append(key, payload[key]));
        formData.images?.forEach((img) => data.append("images", img));

        const res = await axios.post(`${API_URL}/createSKU`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(res.data.message || "SKU created successfully");
      }

      handleCloseModal();
      fetchSKUs();
    } catch (err) {
      console.error("SKU save error:", err);
      toast.error(err.response?.data?.message || "Failed to save SKU");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this SKU?")) return;
    try {
      await axios.delete(`${API_URL}/deleteSKU/${id}`, {
        data: { userId: USER_ID },
      });
      toast.success("SKU deleted successfully");
      fetchSKUs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete SKU");
    }
  };

  const generatePDF = async (sku) => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.height;

    // === HEADER ===
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(33, 150, 243);
    doc.text("SKU REPORT", pageWidth / 2, 20, { align: "center" });

    doc.setDrawColor(33, 150, 243);
    doc.setLineWidth(0.5);
    doc.line(14, 25, pageWidth - 14, 25);

    let yPos = 35;

    // === BASIC DETAILS ===
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(44, 62, 80);
    doc.text("Product Information", 14, yPos);
    yPos += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(60);

    const entryDate = sku.entry_date
      ? new Date(sku.entry_date).toLocaleDateString()
      : "—";
    const exitDate = sku.exit_date
      ? new Date(sku.exit_date).toLocaleDateString()
      : "—";

    const info = [
      ["Product Name", sku.p_name || "—"],
      ["Quantity", sku.qty ?? 0],
      ["Entry Date", entryDate],
      ["Exit Date", exitDate],
    ];

    // Render fields except description
    info.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, 18, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(String(value), 60, yPos, { maxWidth: pageWidth - 70 });
      yPos += 8;
    });

    // Render wrapped description separately
    doc.setFont("helvetica", "bold");
    doc.text("Description:", 18, yPos);
    doc.setFont("helvetica", "normal");
    const descText = doc.splitTextToSize(
      sku.description || "—",
      pageWidth - 70
    );
    doc.text(descText, 60, yPos);
    yPos += descText.length * 6;

    // --- Decorative divider ---
    doc.setDrawColor(200);
    doc.setLineWidth(0.2);
    doc.line(14, yPos + 2, pageWidth - 14, yPos + 2);
    yPos += 10;

    // === IMAGES SECTION ===
    const imgSize = 50;
    if (sku.images?.length) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(44, 62, 80);
      doc.text("Images", 14, yPos);
      yPos += 6;

      let xPos = 14;
      for (let i = 0; i < sku.images.length; i++) {
        if (yPos + imgSize + 10 > pageHeight - 10) {
          doc.addPage();
          yPos = 20;
        }

        const imgUrl = `${API_URL}/uploads/${sku.images[i].imageName}`;
        try {
          const img = await toBase64(imgUrl);
          doc.addImage(img, "JPEG", xPos, yPos, imgSize, imgSize);
        } catch (e) {
          console.warn("Failed to load image:", imgUrl);
        }

        xPos += imgSize + 8;
        if (xPos + imgSize > pageWidth - 14) {
          xPos = 14;
          yPos += imgSize + 10;
        }
      }

      yPos += imgSize + 10;
    } else {
      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text("No images available.", 14, yPos);
      yPos += 10;
    }

    // === FOOTER ===
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth - 20,
        doc.internal.pageSize.height - 10
      );
    }

    doc.save(`${sku.p_name}_SKU_Report.pdf`);
  };

  // --- Helper to Convert Image URL → Base64 ---
  const toBase64 = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = function () {
        const canvas = document.createElement("canvas");
        canvas.width = this.width;
        canvas.height = this.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(this, 0, 0);
        resolve(canvas.toDataURL("image/jpeg"));
      };
      img.onerror = reject;
      img.src = url;
    });
  const userId = Cookies.get("id");
  const [loadingPDF, setLoadingPDF] = useState(false);

  const generateSKUPDFFromBackend = async () => {
    try {
      setLoadingPDF(true);

      const response = await axios.get(`${API_URL}/getAllSKUPDF/${userId}`, {
        responseType: "arraybuffer",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "skus.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingPDF(false);
    }
  };

  const startEntry = totalEntries === 0 ? 0 : (page - 1) * limit + 1;
  const endEntry = Math.min(page * limit, totalEntries);

  return (
    <Box sx={{ p: 4 }}>
      {/* <ToastContainer /> */}
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Manage SKU
      </Typography>

      <Box sx={{ p: 4 }}>
        {/* <ToastContainer /> */}

        {/* Page Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Manage SKU
          </Typography>
          <div style={{ display: "flex", gap: "3px" }}>
            {(ROLE === "admin" || ROLE === "factory_m") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenModal()}
                // sx={{ height: 40 }}
              >
                Add SKU
              </Button>
            )}
            {/* PDF Generation Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={generateSKUPDFFromBackend}
              disabled={loadingPDF} // Disable while loading
              sx={{ position: "relative", minWidth: 180 }}
            >
              {loadingPDF ? (
                <>
                 
                  <span style={{ opacity: 1 }}>Downloading...</span>
                </>
              ) : (
                "Download All SKU PDF"
              )}
            </Button>
          </div>
        </Box>

        {/* Filters Panel */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <TextField
            placeholder="Search catalogs..."
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ minWidth: 200, flex: 1 }}
          />

          <TextField
            label="Start Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => {
              setPage(1);
              setStartDate(e.target.value);
            }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => {
              setPage(1);
              setEndDate(e.target.value);
            }}
          />

          <Button
            variant="contained"
            size="small"
            sx={{ height: 40 }}
            onClick={() => fetchSKUs()}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ height: 40 }}
            onClick={() => {
              setStartDate("");
              setEndDate("");
              setPage(1);
              fetchSKUs();
            }}
          >
            Reset
          </Button>
        </Paper>

        {/* Entries & Pagination */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Select
            value={limit}
            size="small"
            onChange={(e) => setLimit(Number(e.target.value))}
            sx={{ minWidth: 140 }}
          >
            {[5, 10, 25, 50].map((num) => (
              <MenuItem key={num} value={num}>
                {num} entries per page
              </MenuItem>
            ))}
          </Select>

          <Typography variant="body2" sx={{ flex: 1, textAlign: "right" }}>
            Showing {startEntry} to {endEntry} of {totalEntries} entries
          </Typography>
        </Box>

        {/* Table */}
        {loading ? (
          <Box sx={{ textAlign: "center", mt: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "p_name"}
                      direction={sortBy === "p_name" ? sortOrder : "asc"}
                      onClick={() => handleSort("p_name")}
                    >
                      Product Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "entry_date"}
                      direction={sortBy === "entry_date" ? sortOrder : "asc"}
                      onClick={() => handleSort("entry_date")}
                    >
                      Entry Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "exit_date"}
                      direction={sortBy === "exit_date" ? sortOrder : "asc"}
                      onClick={() => handleSort("exit_date")}
                    >
                      Exit Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Images</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {skus.length > 0 ? (
                  skus.map((sku) => (
                    <TableRow key={sku._id} hover>
                      <TableCell>{sku.p_name}</TableCell>

                      {/* Description Cell with Dynamic Wrapping */}
                      <TableCell
                        sx={{
                          wordWrap: "break-word", // Ensure text wraps
                          whiteSpace: "normal", // Allow multiline wrapping
                          maxWidth: 200, // Set a maximum width for the description
                        }}
                      >
                        {sku.description || "—"}
                      </TableCell>

                      <TableCell>{sku.qty ?? 0}</TableCell>

                      <TableCell>
                        {sku.entry_date
                          ? new Date(sku.entry_date).toLocaleDateString()
                          : "—"}
                      </TableCell>

                      <TableCell>
                        {sku.exit_date
                          ? new Date(sku.exit_date).toLocaleDateString()
                          : "—"}
                      </TableCell>

                      {/* Images Cell with Dynamic Layout */}
                      <TableCell>
                        <Grid container spacing={1} alignItems="center">
                          {sku.images?.slice(0, 3).map((img, i) => (
                            <Grid item key={i}>
                              <img
                                src={`${API_URL}/uploads/${img.imageName}`}
                                alt=""
                                width={50}
                                height={50}
                                style={{
                                  objectFit: "cover",
                                  borderRadius: 4,
                                  border: "1px solid #ddd",
                                }}
                              />
                            </Grid>
                          ))}
                          {sku.images?.length > 3 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              +{sku.images.length - 3} more
                            </Typography>
                          )}
                        </Grid>
                      </TableCell>

                      {/* Action Buttons */}
                      <TableCell>
                        {(ROLE === "admin" || ROLE === "factory_m") && (
                          <>
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenModal(sku)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(sku._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                        <IconButton
                          color="secondary"
                          onClick={() => generatePDF(sku)}
                          title="Download PDF"
                        >
                          <PictureAsPdf />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      No SKUs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {totalPages >= 1 && (
          <Pagination page={page} setPage={setPage} totalPages={totalPages} />
        )}
      </Box>

      {/* SKU Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{isEditing ? "Edit SKU" : "Add SKU"}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
              label="Product Name"
              variant="outlined"
              required
              value={formData.p_name}
              onChange={(e) =>
                setFormData({ ...formData, p_name: e.target.value })
              }
            />

            <TextField
              label="Description"
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />

            <TextField
              label="Quantity"
              type="number"
              variant="outlined"
              required
              value={formData.qty}
              onChange={(e) =>
                setFormData({ ...formData, qty: e.target.value })
              }
            />

            <TextField
              label="Entry Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.entry_date}
              onChange={(e) =>
                setFormData({ ...formData, entry_date: e.target.value })
              }
            />
            <TextField
              label="Exit Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.exit_date}
              onChange={(e) =>
                setFormData({ ...formData, exit_date: e.target.value })
              }
            />

            <TextField
              label="Note"
              InputLabelProps={{ shrink: true }}
              value={formData.exit_note}
              onChange={(e) =>
                setFormData({ ...formData, exit_note: e.target.value })
              }
            />

            {isEditing ? (
              <SKUImageManager
                skuId={editId}
                images={formData.imagePreviews}
                onImagesUpdated={(newImages) =>
                  setFormData({ ...formData, imagePreviews: newImages })
                }
              />
            ) : (
              <>
                <Button variant="contained" component="label">
                  Upload Images
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={handleImageChange}
                  />
                </Button>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {formData.imagePreviews.map((src, idx) => (
                    <Grid item key={idx}>
                      <Box
                        sx={{
                          position: "relative",
                          width: 80,
                          height: 80,
                          borderRadius: 2,
                          overflow: "hidden",
                          border: "1px solid #ccc",
                        }}
                      >
                        <img
                          src={src}
                          alt={`preview-${idx}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            bgcolor: "rgba(255,255,255,0.7)",
                          }}
                          onClick={() => {
                            const newImages = [...formData.images];
                            const newPreviews = [...formData.imagePreviews];
                            newImages.splice(idx, 1);
                            newPreviews.splice(idx, 1);
                            setFormData({
                              ...formData,
                              images: newImages,
                              imagePreviews: newPreviews,
                            });
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" variant="contained">
              {isEditing ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default SKU;
